import { Link } from '@tanstack/react-router';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Button } from '@/components/ui/Button';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { usePondElsewhere } from '@/hooks/usePondElsewhere';

interface PondNotInFarmProps {
  pondId: string;
  backTo: '/ponds' | '/entry';
  backLabel: string;
}

/** Shown when a pond URL does not belong to the selected farm (stale tab, shared link). */
export function PondNotInFarm({ pondId, backTo, backLabel }: PondNotInFarmProps) {
  const { currentFarm, setSelectedFarmId } = useCurrentFarm();
  const { data: elsewhere, isLoading, isError, refetch } = usePondElsewhere(pondId);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Looking for this pond...</p>
      </div>
    );
  }

  if (isError) {
    return <ErrorMessage message="Couldn't check your other farms. Please try again." onRetry={() => refetch()} />;
  }

  return (
    <div className="max-w-md mx-auto mt-16 text-center p-8">
      {elsewhere ? (
        <>
          <h1 className="text-lg font-semibold text-gray-900">This pond is in {elsewhere.name}</h1>
          <p className="text-sm text-gray-600 mt-1">
            You are viewing {currentFarm?.name ?? 'another farm'}. Switch farms to open it.
          </p>
          <Button className="mt-5" variant="primary" onClick={() => setSelectedFarmId(elsewhere.id)}>
            Switch to {elsewhere.name}
          </Button>
        </>
      ) : (
        <>
          <h1 className="text-lg font-semibold text-gray-900">Pond not found in this farm</h1>
          <p className="text-sm text-gray-600 mt-1">
            It may have been removed, or the link is for a farm you don't have access to.
          </p>
          <Link
            to={backTo}
            search={backTo === '/entry' ? {} : undefined}
            className="mt-5 inline-flex min-h-11 items-center font-semibold text-primary-600"
          >
            {backLabel}
          </Link>
        </>
      )}
    </div>
  );
}
